import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { MagnifyingGlass, X, Microphone, Copy, ClockCounterClockwise, Check } from "@phosphor-icons/react";
import StatusBadge from "@/components/StatusBadge";
import { dmY } from "@/lib/dateFmt";
import { toast } from "sonner";

/**
 * Global UTR search — admin sees all slips, customer sees only their own.
 * Debounced 300ms, minimum 3 chars, top 25 results.
 *
 * v1.28.20 enhancements:
 *  - Recent searches (last 5 UTRs, stored per browser in localStorage)
 *  - Copy UTR button next to each search result
 *  - Voice search (Web Speech API) — hides mic button when unsupported
 */
const RECENT_KEY = "utr_recent_searches";
const RECENT_MAX = 5;

const loadRecent = () => {
    try {
        const raw = localStorage.getItem(RECENT_KEY);
        if (!raw) return [];
        const arr = JSON.parse(raw);
        return Array.isArray(arr) ? arr.slice(0, RECENT_MAX) : [];
    } catch { return []; }
};

const saveRecent = (list) => {
    try { localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX))); }
    catch { /* localStorage quota / private mode → silent */ }
};

// Best-effort speech-recognition constructor (webkit fallback covers most
// mobile Chrome / Safari builds). Returns null on unsupported browsers so
// the mic button is hidden instead of throwing.
const SpeechRec = typeof window !== "undefined"
    ? (window.SpeechRecognition || window.webkitSpeechRecognition || null)
    : null;

export default function UtrSearch({ isAdmin = false, className = "" }) {
    const [q, setQ] = useState("");
    const [results, setResults] = useState([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [recent, setRecent] = useState(() => loadRecent());
    const [listening, setListening] = useState(false);
    const [copiedId, setCopiedId] = useState("");
    const boxRef = useRef(null);
    const reqIdRef = useRef(0);
    const recogRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        function onDoc(e) {
            if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
        }
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    useEffect(() => {
        if (q.trim().length < 3) {
            setResults([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        const myId = ++reqIdRef.current;
        const t = setTimeout(async () => {
            try {
                const { data } = await api.get("/uploads/search", { params: { q: q.trim() } });
                if (myId !== reqIdRef.current) return;
                setResults(data || []);
            } catch (_e) {
                if (myId !== reqIdRef.current) return;
                setResults([]);
            } finally {
                if (myId === reqIdRef.current) setLoading(false);
            }
        }, 300);
        return () => clearTimeout(t);
    }, [q]);

    const pushRecent = useCallback((utr) => {
        if (!utr) return;
        const clean = String(utr).trim().toUpperCase();
        if (clean.length < 3) return;
        const next = [clean, ...recent.filter((r) => r !== clean)].slice(0, RECENT_MAX);
        setRecent(next);
        saveRecent(next);
    }, [recent]);

    const clear = () => { setQ(""); setResults([]); setOpen(false); };

    const openSlip = (u) => {
        pushRecent(u.utr);
        clear();
        const stamp = Date.now();
        if (isAdmin) navigate(`/admin/uploads?utr=${encodeURIComponent(u.utr)}&_open=${stamp}`);
        else navigate(`/portal/my-uploads?utr=${encodeURIComponent(u.utr)}&_open=${stamp}`);
    };

    const pickRecent = (utr) => {
        setQ(utr);
        setOpen(true);
    };

    const removeRecent = (e, utr) => {
        e.stopPropagation();
        const next = recent.filter((r) => r !== utr);
        setRecent(next);
        saveRecent(next);
    };

    const copyUtr = async (e, utr) => {
        e.stopPropagation();
        if (!utr) return;
        try {
            if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(utr);
            else {
                const ta = document.createElement("textarea");
                ta.value = utr; document.body.appendChild(ta); ta.select();
                document.execCommand("copy"); ta.remove();
            }
            setCopiedId(utr);
            toast.success(`Copied: ${utr}`);
            setTimeout(() => setCopiedId(""), 1400);
        } catch {
            toast.error("Could not copy UTR");
        }
    };

    const toggleVoice = () => {
        if (!SpeechRec) {
            toast.error("Voice search isn't supported on this browser. Try Chrome on Android.");
            return;
        }
        if (listening) {
            try { recogRef.current?.stop(); } catch { /* noop */ }
            setListening(false);
            return;
        }
        try {
            const rec = new SpeechRec();
            rec.lang = "en-IN";
            rec.interimResults = false;
            rec.maxAlternatives = 1;
            rec.onresult = (ev) => {
                const said = ev?.results?.[0]?.[0]?.transcript || "";
                // UTRs are alphanumeric — strip spaces + upper-case what came back
                const utr = said.replace(/\s+/g, "").toUpperCase();
                if (utr) {
                    setQ(utr);
                    setOpen(true);
                }
            };
            rec.onerror = (ev) => {
                setListening(false);
                if (ev?.error && ev.error !== "no-speech" && ev.error !== "aborted") {
                    toast.error(`Voice error: ${ev.error}`);
                }
            };
            rec.onend = () => setListening(false);
            recogRef.current = rec;
            setListening(true);
            rec.start();
        } catch (err) {
            setListening(false);
            toast.error("Could not start voice search");
        }
    };

    const showRecentPanel = open && q.trim().length < 3 && recent.length > 0;
    const showResultsPanel = open && q.trim().length >= 3;

    return (
        <div ref={boxRef} className={`relative ${className}`} data-testid="utr-search">
            <div className="utr-search-glow flex items-center gap-2 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 focus-within:border-blue-400">
                <MagnifyingGlass size={16} className="text-slate-400" />
                <input
                    data-testid="utr-search-input"
                    type="text"
                    value={q}
                    onFocus={() => setOpen(true)}
                    onChange={(e) => { setQ(e.target.value); setOpen(true); }}
                    placeholder="Search by UTR…"
                    className="flex-1 min-w-0 w-40 border-none bg-transparent text-sm outline-none placeholder:text-slate-400 sm:w-56"
                />
                {SpeechRec && (
                    <button
                        type="button"
                        data-testid="utr-search-voice"
                        onClick={toggleVoice}
                        title={listening ? "Listening… tap to stop" : "Voice search"}
                        className={`rounded p-1 transition ${listening ? "bg-rose-500 text-white animate-pulse" : "text-slate-500 hover:bg-slate-100 hover:text-slate-800"}`}
                        aria-label="Voice search"
                    >
                        <Microphone size={13} weight={listening ? "fill" : "regular"} />
                    </button>
                )}
                {q && (
                    <button
                        type="button"
                        data-testid="utr-search-clear"
                        onClick={clear}
                        className="rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                        aria-label="Clear search"
                    >
                        <X size={12} weight="bold" />
                    </button>
                )}
            </div>

            {/* Recent-searches panel — shown when input is empty (or < 3 chars). */}
            {showRecentPanel && (
                <div className="absolute right-0 z-20 mt-2 w-[95vw] max-w-md rounded-lg border border-slate-200 bg-white shadow-xl sm:w-[420px]" data-testid="utr-recent-panel">
                    <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2 text-[11px] uppercase tracking-[0.18em] text-slate-500">
                        <span className="inline-flex items-center gap-1.5">
                            <ClockCounterClockwise size={12} weight="bold" /> Recent
                        </span>
                        <button
                            type="button"
                            data-testid="utr-recent-clear-all"
                            onClick={() => { setRecent([]); saveRecent([]); }}
                            className="text-[10px] normal-case tracking-normal text-slate-400 hover:text-rose-600"
                        >
                            Clear all
                        </button>
                    </div>
                    <ul className="max-h-80 divide-y divide-slate-100 overflow-y-auto">
                        {recent.map((r) => (
                            <li key={r}>
                                <button
                                    type="button"
                                    data-testid={`utr-recent-${r}`}
                                    onClick={() => pickRecent(r)}
                                    className="flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left transition-colors hover:bg-slate-50"
                                >
                                    <span className="truncate font-mono text-[13px] text-slate-800">{r}</span>
                                    <span className="flex items-center gap-1">
                                        <button
                                            type="button"
                                            onClick={(e) => copyUtr(e, r)}
                                            title="Copy UTR"
                                            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                                            data-testid={`utr-recent-copy-${r}`}
                                        >
                                            {copiedId === r ? <Check size={11} weight="bold" className="text-emerald-600" /> : <Copy size={11} weight="bold" />}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={(e) => removeRecent(e, r)}
                                            title="Remove from recent"
                                            className="rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600"
                                            data-testid={`utr-recent-remove-${r}`}
                                        >
                                            <X size={11} weight="bold" />
                                        </button>
                                    </span>
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Live results panel */}
            {showResultsPanel && (
                <div className="absolute right-0 z-20 mt-2 w-[95vw] max-w-md rounded-lg border border-slate-200 bg-white shadow-xl sm:w-[420px]">
                    <div className="border-b border-slate-100 px-3 py-2 text-[11px] uppercase tracking-[0.18em] text-slate-500">
                        {loading ? "Searching…" : `${results.length} result${results.length === 1 ? "" : "s"} for "${q.trim()}"`}
                    </div>
                    {results.length === 0 && !loading && (
                        <div className="px-4 py-6 text-center text-sm text-slate-500" data-testid="utr-search-empty">
                            No payment slips found with that UTR.
                        </div>
                    )}
                    <div className="max-h-80 divide-y divide-slate-100 overflow-y-auto" data-testid="utr-search-results">
                        {results.map((u) => (
                            <div
                                key={u.id}
                                data-testid={`utr-search-item-${u.id}`}
                                className="flex items-center gap-2 px-3 py-3 transition-colors hover:bg-slate-50"
                            >
                                <button
                                    type="button"
                                    onClick={() => openSlip(u)}
                                    className="min-w-0 flex-1 text-left"
                                >
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="truncate font-mono text-[13px] font-semibold text-slate-900">{u.utr || "—"}</span>
                                        <StatusBadge status={u.status} />
                                    </div>
                                    <div className="mt-0.5 flex items-center justify-between gap-2 text-xs text-slate-500">
                                        <span className="truncate">
                                            {isAdmin && u.party_name ? `${u.party_name} · ` : ""}{u.payment_app || "—"} · {u.payment_date ? dmY(u.payment_date) : (u.uploaded_at ? dmY(u.uploaded_at.slice(0, 10)) : "")}
                                        </span>
                                        <span className="tabular-nums font-medium text-slate-800">₹ {Number(u.amount || 0).toLocaleString("en-IN")}</span>
                                    </div>
                                </button>
                                <button
                                    type="button"
                                    data-testid={`utr-search-copy-${u.id}`}
                                    onClick={(e) => copyUtr(e, u.utr)}
                                    title="Copy UTR"
                                    aria-label={`Copy UTR ${u.utr}`}
                                    className="shrink-0 rounded p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-800"
                                >
                                    {copiedId === u.utr ? <Check size={13} weight="bold" className="text-emerald-600" /> : <Copy size={13} weight="bold" />}
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
